"use client";

import { useLocale } from "next-intl";
import React, { useEffect, useState } from "react";

import { cn } from "@/lib/utils";
import IconPlus from "../Icons/IconPlus";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  size?: "small" | "medium" | "large" | "full";
  className?: string;
  closeButtonClassName?: string;
  childrenClassName?: string;
  modalClassName?: string;
  closeIcon?: React.ReactNode;
}

const sizeClasses = {
  small: "max-w-md",
  medium: "max-w-3xl",
  large: "max-w-7xl",
  full: "max-full h-full",
};

const Modal = ({
  isOpen,
  onClose,
  children,
  size = "large",
  className = "",
  closeButtonClassName = "",
  childrenClassName = "",
  modalClassName = "",
  closeIcon,
}: ModalProps) => {
  const locale = useLocale();
  const isRTL = locale === "ar";
  const [shouldAnimate, setShouldAnimate] = useState(false);

  // Default close button classes with RTL support
  const defaultCloseButtonClasses = cn(
    "absolute top-4 cursor-pointer text-black hover:opacity-70",
    isRTL ? "left-4" : "right-4",
  );

  const finalCloseButtonClassName = closeButtonClassName || defaultCloseButtonClasses;

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscKey);
    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure the modal mounts before animating
      const timer = setTimeout(() => setShouldAnimate(true), 10);
      return () => clearTimeout(timer);
    } else {
      setShouldAnimate(false);
    }
  }, [isOpen]);

  // Don't render if not open
  if (!isOpen && !shouldAnimate) return null;

  return (
    <div
      className={`fixed inset-0 z-100000 flex items-center justify-center bg-black/80 backdrop-blur transition-opacity duration-200 ease-in-out ${
        shouldAnimate ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
      } ${modalClassName}`}
      onClick={onClose}
      onKeyDown={(e) => e.key === "Escape" && onClose()}
      tabIndex={0}
      role="button"
      aria-label="Close modal overlay"
    >
      <div
        className={`relative w-full transform rounded-lg bg-white p-4 shadow-lg transition-all duration-200 ease-in-out ${sizeClasses[size]} ${className} ${
          shouldAnimate ? "scale-100 opacity-100" : "scale-95 opacity-0"
        }`}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.key === "Enter" && e.stopPropagation()}
        tabIndex={0}
        role="button"
        aria-label="Modal content"
      >
        <button aria-label="Close modal" onClick={onClose} className={finalCloseButtonClassName}>
          {closeIcon ?? <IconPlus className="rotate-45" />}
        </button>
        <div className={`max-h-[90vh] overflow-auto ${childrenClassName}`}>{children}</div>
      </div>
    </div>
  );
};

export default Modal;
